import { useEffect, useMemo, useState } from "react";
import { Wifi, WifiOff, Printer, AlertCircle, RotateCw, Download } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { usePWA } from "@/hooks/usePWA";
import { useVersionCheck } from "@/hooks/useVersionCheck";
import { useConnectivity } from "@/hooks/useConnectivity";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Declare build time constant injected by Vite
declare const __BUILD_TIME__: string;

interface ConnectionStatusIndicatorProps {
  className?: string;
}

export function ConnectionStatusIndicator({ className }: ConnectionStatusIndicatorProps) {
  const { activeRestaurant } = useRestaurantContext();
  const { forceUpdate, isUpdating } = usePWA();
  const { isOutdated } = useVersionCheck();
  const { isOnline } = useConnectivity();
  const [pendingJobsCount, setPendingJobsCount] = useState(0);

  // Format build time for display
  const buildVersion = useMemo(() => {
    try {
      const date = new Date(__BUILD_TIME__);
      return date.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "dev";
    }
  }, []);

  // Fetch pending print jobs count
  useEffect(() => {
    if (!activeRestaurant?.id) return;

    const fetchPendingJobs = async () => {
      const { count, error } = await supabase
        .from("pending_print_jobs")
        .select("*", { count: "exact", head: true })
        .eq("restaurant_id", activeRestaurant.id)
        .eq("status", "pending");

      if (!error && count !== null) {
        setPendingJobsCount(count);
      }
    };

    // Fetch initially
    fetchPendingJobs();

    // Setup realtime subscription
    const channel = supabase
      .channel("pending-jobs-count")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "pending_print_jobs",
          filter: `restaurant_id=eq.${activeRestaurant.id}`,
        },
        () => {
          fetchPendingJobs();
        }
      )
      .subscribe();

    // Poll every 30 seconds as backup
    const interval = setInterval(fetchPendingJobs, 30000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(interval);
    };
  }, [activeRestaurant?.id]);

  return (
    <TooltipProvider>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className={cn(
              "h-8 px-2 gap-1.5 text-primary-foreground hover:bg-primary-foreground/20",
              className
            )}
          >
            {/* Online/Offline Status */}
            <div
              className={cn(
                "flex items-center justify-center h-5 w-5 rounded-full transition-colors",
                isOnline
                  ? "bg-emerald-500/30 text-emerald-300"
                  : "bg-destructive/30 text-red-300 animate-pulse"
              )}
            >
              {isOnline ? (
                <Wifi className="h-3 w-3" />
              ) : (
                <WifiOff className="h-3 w-3" />
              )}
            </div>

            {/* Pending Print Jobs Badge */}
            {pendingJobsCount > 0 && (
              <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500/30 text-amber-200">
                <Printer className="h-3 w-3" />
                <span className="text-xs font-medium">{pendingJobsCount}</span>
              </div>
            )}

            {/* Update available indicator */}
            {isOutdated && (
              <div className="flex items-center justify-center h-5 w-5 rounded-full bg-blue-500/30 text-blue-300 animate-pulse">
                <Download className="h-3 w-3" />
              </div>
            )}

            {/* Warning if offline with pending jobs */}
            {!isOnline && pendingJobsCount > 0 && (
              <AlertCircle className="h-4 w-4 text-red-300" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <div className="px-2 py-1.5">
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  "h-2 w-2 rounded-full",
                  isOnline ? "bg-emerald-500" : "bg-destructive"
                )}
              />
              <span className="text-sm font-medium">
                {isOnline ? "Conectado" : "Sem conexão"}
              </span>
            </div>
            {pendingJobsCount > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                {pendingJobsCount} impressão(ões) na fila
              </p>
            )}
          </div>
          {isOutdated && (
            <>
              <DropdownMenuSeparator />
              <div className="px-2 py-1.5 bg-blue-50 dark:bg-blue-950/50 rounded-sm mx-1">
                <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
                  <Download className="h-4 w-4" />
                  <span className="text-sm font-medium">Atualização disponível</span>
                </div>
              </div>
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={forceUpdate}
            disabled={isUpdating}
            className={cn("gap-2", isOutdated && "text-blue-600 dark:text-blue-400 font-medium")}
          >
            <RotateCw className={cn("h-4 w-4", isUpdating && "animate-spin")} />
            {isUpdating ? "Atualizando..." : isOutdated ? "Atualizar Agora" : "Forçar Atualização"}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <div className="px-2 py-1.5">
            <p className="text-xs text-muted-foreground">
              Versão: <span className="font-mono">{buildVersion}</span>
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Use "Forçar Atualização" se o app estiver desatualizado.
            </p>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </TooltipProvider>
  );
}
